import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { AboutMe, getAboutMe, updateAboutMe } from "../../ApiService/admin/AboutMe";
import { adminAvatarFallback, imageUrl } from "../../utils/image";
import PageHeader from "./PageHeader";
import FileField from "./FileField";
import JalaliDateInput from "./JalaliDateInput";
import { useAdminLang } from "./i18n";

type Status = "loading" | "idle" | "saving" | "saved" | "error" | "load-error";

const EMPTY: AboutMe = {
  id: 0,
  name: "",
  jobTitle: "",
  description: "",
  email: "",
  birthDate: "",
  phone: "",
  workEx: 0,
  completedProjects: 0,
  happyClients: 0,
  residence: "",
  avatarName: "",
  resumeFileName: "",
  githubUrl: "",
  telegramId: "",
  responseTime: "",
};

export default function AboutMeEditor() {
  const [form, setForm] = useState<AboutMe>(EMPTY);
  const [status, setStatus] = useState<Status>("loading");
  const [validated, setValidated] = useState(false);
  const { t } = useAdminLang();

  useEffect(() => {
    getAboutMe()
      .then((data) => {
        if (data) {
          // null from the API -> "" so every input stays controlled
          const filled = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? ""]));
          setForm({ ...EMPTY, ...filled, birthDate: data.birthDate ? data.birthDate.slice(0, 10) : "" });
        }
        setStatus("idle");
      })
      .catch((err) => {
        console.error(err);
        setStatus("load-error");
      });
  }, []);

  useEffect(() => {
    if (status !== "saved") return;
    const timer = setTimeout(() => setStatus("idle"), 3000);
    return () => clearTimeout(timer);
  }, [status]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "number" ? Number(value) : value }));
  };

  const setField = (name: keyof AboutMe, value: string) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);
    if (!e.currentTarget.checkValidity()) return;

    setStatus("saving");
    try {
      await updateAboutMe(form);
      setStatus("saved");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const required = <span className="text-danger">*</span>;

  return (
    <>
      <PageHeader title="درباره من" />

      {status === "loading" ? (
        <div className="text-center p-5"><div className="spinner-border text-primary"></div></div>
      ) : (
        <div className="row">
          <div className="col-xl-4">
            <div className="card custom-card text-center">
              <div className="card-body">
                <span className="avatar avatar-xxl avatar-rounded mb-3">
                  <img src={imageUrl(form.avatarName, adminAvatarFallback(form.name || "Ali", 128))} alt={form.name} />
                </span>
                <h5 className="fw-semibold mb-1">{form.name || "—"}</h5>
                <p className="text-muted mb-0">{t("این اطلاعات در بخش‌های «خانه»، «درباره من» و «تماس» سایت نمایش داده می‌شود.")}</p>
              </div>
            </div>
          </div>

          <div className="col-xl-8">
            <div className="card custom-card">
              <div className="card-header">
                <div className="card-title"><i className="ri-user-3-line me-2 align-middle"></i>{t("اطلاعات شخصی")}</div>
              </div>
              <div className="card-body">
                {status === "load-error" && (
                  <div className="alert alert-warning">{t("اطلاعات فعلی از سرور دریافت نشد؛ فرم خالی نمایش داده شده است.")}</div>
                )}
                {status === "saved" && <div className="alert alert-success">{t("تغییرات با موفقیت ذخیره شد.")}</div>}
                {status === "error" && <div className="alert alert-danger">{t("ذخیره اطلاعات انجام نشد. دوباره تلاش کنید.")}</div>}

                <form className={`row g-3 ${validated ? "was-validated" : ""}`} onSubmit={handleSubmit} noValidate>
                  <div className="col-md-6">
                    <label htmlFor="about-name" className="form-label">{t("نام و نام خانوادگی")} {required}</label>
                    <input id="about-name" name="name" className="form-control" maxLength={130} value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-jobTitle" className="form-label">{t("عنوان شغلی")}</label>
                    <input id="about-jobTitle" name="jobTitle" dir="ltr" className="form-control" maxLength={100} value={form.jobTitle} onChange={handleChange} />
                    <div className="form-text">{t("زیر نام در لوگو، مثلا Software Developer")}</div>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="about-workEx" className="form-label">{t("سال‌های تجربه کاری")} {required}</label>
                    <input id="about-workEx" name="workEx" type="number" min={0} max={100} className="form-control" value={form.workEx} onChange={handleChange} required />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="about-completedProjects" className="form-label">{t("پروژه موفق")}</label>
                    <input id="about-completedProjects" name="completedProjects" type="number" min={0} className="form-control" value={form.completedProjects} onChange={handleChange} />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="about-happyClients" className="form-label">{t("مشتری راضی")}</label>
                    <input id="about-happyClients" name="happyClients" type="number" min={0} className="form-control" value={form.happyClients} onChange={handleChange} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="about-responseTime" className="form-label">{t("حداکثر زمان پاسخ")}</label>
                    <input id="about-responseTime" name="responseTime" className="form-control" maxLength={30} value={form.responseTime ?? ""} onChange={handleChange} />
                    <div className="form-text">{t("آخرین باکس آمار صفحه اول، مثلا ۲۴ ساعت. خالی باشد نمایش داده نمی‌شود.")}</div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-email" className="form-label">{t("ایمیل")} {required}</label>
                    <input id="about-email" name="email" type="email" dir="ltr" className="form-control" maxLength={100} value={form.email} onChange={handleChange} required />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-phone" className="form-label">{t("تلفن")} {required}</label>
                    <input id="about-phone" name="phone" type="tel" dir="ltr" className="form-control" maxLength={20} value={form.phone} onChange={handleChange} required />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-githubUrl" className="form-label">{t("لینک گیت‌هاب")}</label>
                    <input id="about-githubUrl" name="githubUrl" type="url" dir="ltr" className="form-control" maxLength={200} value={form.githubUrl} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-telegramId" className="form-label">{t("آیدی تلگرام")}</label>
                    <input id="about-telegramId" name="telegramId" dir="ltr" className="form-control" maxLength={32} pattern="[A-Za-z0-9_]{5,32}" value={form.telegramId} onChange={handleChange} />
                    <div className="form-text">{t("بدون @، مثلا alir431")}</div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-birthDate" className="form-label">{t("تاریخ تولد")} {required}</label>
                    <JalaliDateInput id="about-birthDate" value={form.birthDate ?? ""} onChange={(v) => setField("birthDate", v)} required />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="about-residence" className="form-label">{t("محل سکونت")} {required}</label>
                    <input id="about-residence" name="residence" className="form-control" maxLength={100} value={form.residence} onChange={handleChange} required />
                  </div>
                  <div className="col-12">
                    <label htmlFor="about-avatar" className="form-label">{t("عکس پروفایل")}</label>
                    <FileField id="about-avatar" kind="image" value={form.avatarName ?? ""} onChange={(v) => setField("avatarName", v)} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="about-resume" className="form-label">{t("فایل رزومه (PDF)")}</label>
                    <FileField id="about-resume" kind="resume" value={form.resumeFileName ?? ""} onChange={(v) => setField("resumeFileName", v)} />
                    <div className="form-text">{t("دکمه «دانلود رزومه» سایت همین فایل را دانلود می‌کند.")}</div>
                  </div>
                  <div className="col-12">
                    <label htmlFor="about-description" className="form-label">{t("متن درباره من")} {required}</label>
                    <textarea id="about-description" name="description" rows={6} maxLength={2000} className="form-control" value={form.description} onChange={handleChange} required />
                    <div className="form-text">{t("هر خط یک پاراگراف جدا در سایت می‌شود.")}</div>
                  </div>
                  <div className="col-12 mt-4">
                    <button className="btn btn-primary" type="submit" disabled={status === "saving"}>
                      {status === "saving" ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>{t("در حال ذخیره...")}
                        </>
                      ) : (
                        t("ذخیره تغییرات")
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
